import { motion } from "framer-motion";
import { Crosshair, Layers, Gem } from "lucide-react";
import { TELEGRAM_URL } from "../i18n";
import { trackCTA } from "../lib/track";

const icons = [Crosshair, Layers, Gem];

export const IndicatorShowcase = ({ t }) => (
  <section id="indicator" data-testid="indicator-section" className="relative overflow-hidden py-24 lg:py-32">
    <div className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-[#2847C8]/20 blur-[120px]" />
    <div className="mx-auto max-w-7xl px-6">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6"
        >
          <div className="flex items-center gap-3">
            <p className="font-mono-num text-xs uppercase tracking-[0.3em] text-[#EAB308]">{t.indicator.eyebrow}</p>
            <span className="rounded-full bg-[#2847C8] px-3 py-1 font-mono-num text-[10px] font-bold tracking-[0.2em] text-white">
              {t.indicator.tag}
            </span>
          </div>
          <h2 className="mt-4 text-4xl font-extrabold sm:text-5xl">{t.indicator.headline}</h2>
          <p className="mt-4 text-base text-gray-400 md:text-lg">{t.indicator.sub}</p>

          <div className="mt-10 space-y-6">
            {t.indicator.bullets.map((b, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={i}
                  data-testid={`indicator-bullet-${i}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2847C8]/25">
                    <Icon className="h-5 w-5 text-[#8FA8FF]" />
                  </div>
                  <div>
                    <h3 className="font-bold">{b.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-gray-400">{b.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTA("indicator")}
            data-testid="indicator-cta-button"
            className="mt-10 inline-flex items-center rounded-full bg-[#EAB308] px-8 py-3.5 font-bold text-[#0B0E11] transition-transform duration-300 hover:scale-105"
          >
            {t.indicator.cta}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-6"
        >
          <div className="relative mx-auto max-w-md">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#2847C8]/50 via-transparent to-[#EAB308]/30 blur-xl" />
            <div className="relative overflow-hidden rounded-[1.6rem] border border-[#2847C8]/50 bg-[#0C1732] shadow-2xl">
              <img
                src="/indicator-xauusd.jpeg"
                alt="GT ZONE V3 indicator on XAUUSD chart"
                loading="lazy"
                data-testid="indicator-chart-image"
                className="w-full"
              />
            </div>
            <p data-testid="indicator-caption" className="mt-4 text-center font-mono-num text-xs text-gray-500">
              {t.indicator.caption}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
